import React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

interface DailyProductionReportProps {
  callSheetRawData: any[];
  currentShootingDay: string;
  realStartAndEnd: any[];
  realTakes?: any[];
  clapperCatalog: any[];
}

const getTimeDifferenceInMinutes = (time1: string, time2: string): number => {
  if (!time1 || !/^\d{2}:\d{2}$/.test(time1) || !time2 || !/^\d{2}:\d{2}$/.test(time2)) {
    return 0;
  }

  const [h1, m1] = time1.split(':').map(Number);
  const [h2, m2] = time2.split(':').map(Number);

  const date1 = new Date();
  date1.setHours(h1, m1, 0, 0);

  const date2 = new Date();
  date2.setHours(h2, m2, 0, 0);

  if (date2 < date1) {
    return 0;
  }

  const diff = date2.getTime() - date1.getTime();
  return Math.floor(diff / (1000 * 60));
};

export function DailyProductionReport ({ clapperCatalog, realStartAndEnd, callSheetRawData, currentShootingDay, realTakes }: DailyProductionReportProps) {
  const filteredData = currentShootingDay === 'All Days'
    ? callSheetRawData
    : callSheetRawData?.filter(item => item.Day.toString() === currentShootingDay);

  const allPlanesEmpty = filteredData?.every((item: any) => !item.Plane || item.Plane === '');
  let dataToRender = filteredData;

  if (allPlanesEmpty) {
    const filteredClapperCatalog = currentShootingDay === 'All Days'
      ? clapperCatalog
      : clapperCatalog?.filter(item => item.dia_rodaje && item.dia_rodaje.toString() === currentShootingDay);

    if (filteredClapperCatalog?.length) {
      dataToRender = filteredData.flatMap(callSheetRow => {
        const planesForSequence = filteredClapperCatalog.filter(
          clapperRow => String(clapperRow.sequence) === String(callSheetRow.Sequence)
        );

        if (planesForSequence.length > 0) {
          const uniquePlanes = Array.from(new Map(planesForSequence.map(item => [item.plane, item])).values());
          return uniquePlanes.map(plane => ({
            ...callSheetRow,
            Plane: plane.plane,
          }));
        }
        
        return callSheetRow;
      });
    }
  }

  const processedData = dataToRender?.map((row: any) => {
    const sequenceData = realStartAndEnd.find(
      (item: any) => String(item.sequence) === String(row.Sequence) && String(item.plane) === String(row.Plane)
    );
  
    const takesData = realTakes?.find(
      (item: any) => String(item.sequence) === String(row.Sequence) && String(item.plane) === String(row.Plane)
    );
  
    const numberOfTakes = takesData ? takesData.takes : 'N/A';
  
    const realStartTime = sequenceData?.realStart ? new Date(sequenceData.realStart).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 'N/A';
    const realEndTime = sequenceData?.realEnd ? new Date(sequenceData.realEnd).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 'N/A';
    
    return {
      ...row,
      numberOfTakes,
      realStartTime,
      realEndTime,
      realStartRaw: sequenceData?.realStart,
    };
  });
  
  processedData?.sort((a, b) => {
    const dayComparison = Number(a.Day) - Number(b.Day);
    if (dayComparison !== 0) return dayComparison;
  
    if (a.realStartRaw && b.realStartRaw) {
      return new Date(a.realStartRaw).getTime() - new Date(b.realStartRaw).getTime();
    }
    if (a.realStartRaw) return -1;
    if (b.realStartRaw) return 1;
  
    return a.startTime.localeCompare(b.startTime);
  });
  console.log(processedData);
  return (
    <TableContainer component={Paper}>
      <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
              <TableRow>
                  <TableCell>Scene</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Shot</TableCell>
                  <TableCell>Planned Start</TableCell>
                  <TableCell>Real Start</TableCell>
                  <TableCell>Planned End</TableCell>
                  <TableCell>Real End</TableCell>
                  <TableCell>Takes</TableCell>
              </TableRow>
          </TableHead>
          <TableBody>
            {processedData?.map((row: any, index: number) => {
              let startDiff = getTimeDifferenceInMinutes(row.startTime, row.realStartTime);
              const endDiff = getTimeDifferenceInMinutes(row.endTime, row.realEndTime);
              
              if(row.Plane.toString() !== 1) {
                startDiff = 0;
              }

              const maxDiff = Math.max(startDiff, endDiff);

              let rowColor = 'inherit';
              if(row.realStartTime  === 'N/A' || row.realEndTime === 'N/A') {
                rowColor = 'inherit';
              } else if (maxDiff <= 40) {
                rowColor = '#66BB6A'; // Softer Green
              } else if (maxDiff <= 70) {
                rowColor = '#FFAA6E'; // Softer Yellow
              } else {
                rowColor = '#EF5350'; // Softer Red
              }

              return (
                <TableRow
                    key={`${row.Sequence}-${row.Plane}-${index}`}
                    sx={{ backgroundColor: rowColor }}
                >
                    <TableCell>
                        {row.Sequence}
                    </TableCell>
                    <TableCell>
                        {row.location}
                    </TableCell>
                    <TableCell>
                      {row.Plane}
                    </TableCell>
                    <TableCell>{row.startTime}</TableCell>
                    <TableCell>{row.realStartTime}</TableCell>
                    <TableCell>{row.endTime}</TableCell>
                    <TableCell>{row.realEndTime}</TableCell>
                    <TableCell>{row.numberOfTakes}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
      </Table>
  </TableContainer>
  );
}
